import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Sprout } from 'lucide-react'
import { TENANT_LANDING } from '@/config/tenantLanding'
import { BRANDING } from '@/config/branding'

export function Footer() {
  const { t } = useTranslation('nav')
  const { t: tc } = useTranslation('common')
  const { enableCommunity = true } = useFlags()
  const { footer, footerSproutMark, footerArkansasLayout, footerLight, footerTagline } = TENANT_LANDING
  const { footerAccentColor } = BRANDING
  const headingClass = footerLight
    ? 'text-sm font-semibold text-primary-500 mb-2'
    : footerArkansasLayout
    ? 'text-sm font-semibold text-[#8eb8d4] mb-4'
    : footerSproutMark
      ? 'text-white font-semibold mb-2 text-xs uppercase tracking-wider'
      : 'text-white font-semibold mb-2 text-xs'
  const footerLinkClass = footerLight
    ? 'text-sm text-gray-700 hover:text-primary-500 transition-colors'
    : footerArkansasLayout
    ? 'text-sm text-white/55 hover:text-white transition-colors'
    : footerAccentColor
      ? 'footer-link text-xs text-gray-400 transition-colors'
      : 'text-xs text-gray-400 hover:text-white transition-colors'

  const awsBadge = (
    <a
      href="https://aws.amazon.com/what-is-cloud-computing"
      target="_blank"
      rel="noopener noreferrer"
      className="flex-shrink-0 opacity-90 transition-opacity hover:opacity-100"
    >
      <img
        src={footerLight
          ? 'https://d0.awsstatic.com/logos/powered-by-aws.png'
          : 'https://d0.awsstatic.com/logos/powered-by-aws-white.png'}
        alt="Powered by AWS Cloud Computing"
        className="h-10 w-auto object-contain"
      />
    </a>
  )

  return (
    <footer
      style={footerAccentColor && !footerArkansasLayout
        ? { backgroundColor: footer.bg, borderTopColor: footerAccentColor }
        : { backgroundColor: footer.bg }}
      className={footerLight
        ? 'border-t border-gray-300'
        : footerAccentColor && !footerArkansasLayout ? 'border-t-4' : footerArkansasLayout ? '' : 'border-t border-gray-600'}
    >
      <div className={footerArkansasLayout
        ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'
        : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'}
      >
        {footerLight && (
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              {footer.logoUrl && (
                <img
                  src={footer.logoUrl}
                  alt={footer.logoAlt}
                  className="h-12 w-auto object-contain"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              )}
              <p className="text-base font-semibold text-primary-500 m-0">{footer.orgName}</p>
            </div>
            {awsBadge}
          </div>
        )}

        {!footerSproutMark && !footerArkansasLayout && !footerLight && (
          <div className="flex items-center justify-between gap-4 mb-2">
            {footer.logoUrl && (
              <img
                src={footer.logoUrl}
                alt={footer.logoAlt}
                className="h-10 w-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            )}
            {awsBadge}
          </div>
        )}

        <div className={footerArkansasLayout ? 'grid grid-cols-1 md:grid-cols-4 gap-10 mb-10' : 'grid grid-cols-1 md:grid-cols-4 gap-6 mb-4'}>
          <div className="col-span-1">
            {footerArkansasLayout ? (
              <div className="flex items-center gap-3 mb-4">
                {footer.logoUrl && (
                  <img
                    src={footer.logoUrl}
                    alt={footer.logoAlt}
                    className="h-10 w-10 rounded-md object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                  />
                )}
                <span className="text-lg font-semibold tracking-tight font-manrope">
                  <span className="text-white">Develop</span>
                  <span className="text-[#8ec8e0]">Works</span>
                </span>
              </div>
            ) : footerSproutMark && (
              <div className="flex items-center gap-2 mb-3">
                <Sprout className="h-6 w-6 text-ar-gold" />
                <span className="text-sm tracking-tight text-white">
                  <span className="font-bold">Develop</span>
                  Works
                </span>
              </div>
            )}
            <p className={footerLight
              ? 'text-sm text-gray-700'
              : footerArkansasLayout ? 'text-sm leading-relaxed text-white/50 max-w-[16.5rem]' : 'text-xs text-gray-400'}>
              {footerTagline ?? t('trustedPlatform')}
            </p>
            {footerSproutMark && (
              <a
                href="https://aws.amazon.com/what-is-cloud-computing"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-gray-300"
              >
                {tc('poweredBy')} AWS
              </a>
            )}
          </div>

          <div>
            <h4 className={headingClass}>{t('platform')}</h4>
            <ul className={footerArkansasLayout ? 'space-y-2' : 'space-y-1'}>
              <li>
                <a href="/catalog" className={footerLinkClass}>
                  {t('browseEvents')}
                </a>
              </li>
              <li>
                <a href="/registration-history" className={footerLinkClass}>
                  {t('certificates')}
                </a>
              </li>
              <li>
                <a href="/transcript" className={footerLinkClass}>
                  {t('transcripts')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={headingClass}>{t('resources')}</h4>
            <ul className={footerArkansasLayout ? 'space-y-2' : 'space-y-1'}>
              <li>
                <a href="https://esc4.net/escdevelopworks/#resources" target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                  {t('helpCenter')}
                </a>
              </li>
              {enableCommunity && (
                <li>
                  <Link to="/community" className={footerLinkClass}>
                    {t('community')}
                  </Link>
                </li>
              )}
              <li>
                <a href="https://escworks.atlassian.net/servicedesk/customer/portal/6/group/24/create/76" target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                  {t('support')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={headingClass}>{t('company')}</h4>
            <ul className={footerArkansasLayout ? 'space-y-2' : 'space-y-1'}>
              <li>
                <a href={footer.aboutUsUrl} target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                  {t('aboutUs')}
                </a>
              </li>
              <li>
                <a href="https://esc4.net/escdevelopworks/#alert" target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                  {t('contact')}
                </a>
              </li>
              <li>
                <a href={footer.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                  {t('privacy')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className={footerLight
          ? 'border-t border-gray-300 pt-4 text-center'
          : footerArkansasLayout ? 'border-t border-white/10 pt-6 text-center' : 'border-t border-gray-600 pt-4 text-center'}>
          <p className={footerLight ? 'text-sm text-gray-700' : footerArkansasLayout ? 'text-sm text-white/40' : 'text-xs text-gray-400'}>
            {footer.address}
          </p>
          <p className={footerLight ? 'text-sm text-gray-700 mt-1' : footerArkansasLayout ? 'text-sm text-white/40 mt-1' : 'text-xs text-gray-400 mt-1'}>
            © {new Date().getFullYear()} {footer.orgName}. {tc('allRightsReserved')}
          </p>
        </div>
      </div>
    </footer>
  )
}
